import type {
  MpClient,
  MpEvent,
  MpPlayer,
  MpPositionTick,
  MpRoomState,
} from "./types";
import { MAX_PLAYERS } from "./protocol";

/** Post-round level vote (mirror of RaceRoom). Keep LEVEL_COUNT in sync with
 *  TEACHING_LEVELS in apps/web/src/data/teaching-levels.ts. */
const VOTE_MS = 30000;
const LEVEL_COUNT = 11;

/**
 * Same-machine multi-tab transport using BroadcastChannel.
 *
 * Roles:
 * - The first peer to call join({asHost:true}) becomes host.
 * - The host owns authoritative `MpRoomState` (player list, phase, seed).
 * - Other peers send `hello` and apply received `state` snapshots.
 *
 * Position ticks flow peer-to-peer (every peer broadcasts its own tick;
 * other peers consume them as snapshots).
 *
 * Production path: swap this for a `WebSocketTransport` that talks to the
 * Cloudflare `RaceRoom` Durable Object. The MpClient interface is identical
 * so callers don't change.
 */
export class BroadcastTransport implements MpClient {
  private bc: BroadcastChannel | null = null;
  private state: MpRoomState | null = null;
  private myId = "";
  private isHost = false;
  private listeners: Array<(e: MpEvent) => void> = [];
  private voteTimer: number | null = null;

  async join(opts: {
    code: string;
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: import("@shared/types").AvatarConfig };
    lessonId?: string;
    seed?: number;
  }): Promise<MpRoomState> {
    this.myId = opts.me.id;
    this.isHost = opts.asHost;
    this.bc = new BroadcastChannel(`wr-room-${opts.code}`);
    this.bc.onmessage = (ev) => this.onMessage(ev.data);

    if (opts.asHost) {
      // Host establishes the room.
      this.state = {
        code: opts.code,
        hostId: this.myId,
        lessonId: opts.lessonId ?? "",
        seed: opts.seed ?? Math.floor(Math.random() * 1e9),
        phase: "lobby",
        players: [
          {
            ...opts.me,
            slotIdx: 0,
            ready: false,
            finished: false,
          },
        ],
      };
      this.broadcastState();
      return this.state;
    }
    // Joiner: ask the host for current state.
    return new Promise<MpRoomState>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.bc?.close();
        this.bc = null;
        reject(new Error("Room not found — check the code or ask the host to open it"));
      }, 1500);
      const off = this.on("event", (e) => {
        if (e.type === "state" && e.state.code === opts.code) {
          clearTimeout(timer);
          off();
          // Add ourselves to host's roster.
          this.bc!.postMessage({
            type: "hello",
            from: this.myId,
            me: opts.me,
          });
          // Wait for host to acknowledge with state including us.
          const off2 = this.on("event", (ev) => {
            if (ev.type === "state" && ev.state.players.some((p) => p.id === this.myId)) {
              off2();
              resolve(ev.state);
            }
          });
        }
      });
      // Probe the channel — host will reply with current state.
      this.bc!.postMessage({ type: "probe", from: this.myId });
    });
  }

  setReady(ready: boolean): void {
    if (!this.bc) return;
    if (this.isHost && this.state) {
      const me = this.state.players.find((p) => p.id === this.myId);
      if (me) me.ready = ready;
      this.broadcastState();
    } else {
      this.bc.postMessage({ type: "set-ready", from: this.myId, ready });
    }
  }

  startRace(): void {
    if (!this.isHost || !this.state) return;
    this.state.phase = "countdown";
    this.state.countdownStartedAt = Date.now();
    this.broadcastState();
    setTimeout(() => {
      if (!this.state) return;
      this.state.phase = "racing";
      this.broadcastState();
    }, 3000);
  }

  backToLobby(): void {
    if (!this.isHost || !this.state) return;
    this.clearVote();
    this.state.phase = "lobby";
    this.state.countdownStartedAt = undefined;
    for (const p of this.state.players) {
      p.ready = false;
      p.finished = false;
      p.finishTimeMs = undefined;
      p.finishScore = undefined;
      p.finishCorrect = undefined;
      p.finishTotal = undefined;
    }
    this.broadcastState();
  }

  changeLesson(lessonId: string): void {
    if (!this.isHost || !this.state) return;
    if (this.state.phase !== "lobby") return;
    this.state.lessonId = lessonId;
    for (const p of this.state.players) p.ready = false;
    this.broadcastState();
  }

  nextLevel(lessonId: string): void {
    if (!this.isHost || !this.state) return;
    this.clearVote();
    // Same as back-to-lobby followed by change-lesson, but atomic.
    this.state.phase = "lobby";
    this.state.countdownStartedAt = undefined;
    this.state.lessonId = lessonId;
    for (const p of this.state.players) {
      p.ready = false;
      p.finished = false;
      p.finishTimeMs = undefined;
      p.finishScore = undefined;
      p.finishCorrect = undefined;
      p.finishTotal = undefined;
    }
    this.broadcastState();
  }

  setHostPaused(paused: boolean): void {
    if (!this.isHost || !this.state) return;
    if (this.state.phase !== "racing") return;
    this.state.hostPaused = !!paused;
    this.broadcastState();
  }

  restartSameLevel(): void {
    if (!this.isHost || !this.state) return;
    this.clearVote();
    for (const p of this.state.players) {
      p.ready = true; p.finished = false;
      p.finishTimeMs = undefined; p.finishScore = undefined;
      p.finishCorrect = undefined; p.finishTotal = undefined;
    }
    this.state.phase = "countdown";
    this.state.countdownStartedAt = Date.now();
    this.broadcastState();
    setTimeout(() => {
      if (this.state?.phase === "countdown") {
        this.state.phase = "racing";
        this.broadcastState();
      }
    }, 3000);
  }

  vote(lessonId: string): void {
    if (this.isHost) {
      this.applyVote(this.myId, lessonId);
    } else {
      this.bc?.postMessage({ type: "vote", from: this.myId, lessonId });
    }
  }

  setQuizStyle(quizStyle: string): void {
    if (this.isHost && this.state) {
      this.state.quizStyle = quizStyle;
      this.broadcastState();
    } else {
      this.bc?.postMessage({ type: "set-quiz-style", from: this.myId, quizStyle });
    }
  }

  sendTick(tick: MpPositionTick): void {
    this.bc?.postMessage({ type: "tick", from: this.myId, tick });
  }

  reportFinish(opts: {
    timeMs: number;
    score: number;
    correctCount: number;
    totalQuestions: number;
  }): void {
    this.bc?.postMessage({
      type: "finish",
      from: this.myId,
      timeMs: opts.timeMs,
      score: opts.score,
      correctCount: opts.correctCount,
      totalQuestions: opts.totalQuestions,
    });
    if (this.isHost) this.applyFinish(this.myId, opts);
  }

  on(_event: "event", cb: (e: MpEvent) => void): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  leave(): void {
    if (this.bc) {
      this.bc.postMessage({ type: "leave", from: this.myId });
      this.bc.close();
      this.bc = null;
    }
    this.state = null;
    this.listeners = [];
  }

  // ─── internals ────────────────────────────────────────────────────────

  private emit(e: MpEvent) {
    for (const l of this.listeners) l(e);
  }

  private broadcastState() {
    if (!this.state) return;
    this.bc?.postMessage({ type: "state", state: this.state });
    this.emit({ type: "state", state: cloneState(this.state) });
  }

  private onMessage(msg: { type: string; [k: string]: unknown }) {
    if (msg.type === "tick") {
      this.emit({
        type: "tick",
        from: msg.from as string,
        tick: msg.tick as MpPositionTick,
      });
      return;
    }
    if (msg.type === "leave") {
      if (this.isHost && this.state) {
        this.state.players = this.state.players.filter((p) => p.id !== msg.from);
        this.broadcastState();
      }
      return;
    }
    if (this.isHost && this.state) {
      switch (msg.type) {
        case "probe":
          this.broadcastState();
          break;
        case "hello": {
          const me = msg.me as MpPlayer;
          if (this.state.players.some((p) => p.id === me.id)) break;
          if (this.state.players.length >= MAX_PLAYERS) {
            // Host-side reject — broadcast an error to the joiner.
            this.bc?.postMessage({ type: "error", to: me.id, message: `Room is full (${MAX_PLAYERS} max).` });
            break;
          }
          const used = new Set(this.state.players.map((p) => p.slotIdx));
          let slotIdx = 0;
          while (used.has(slotIdx)) slotIdx++;
          this.state.players.push({ ...me, slotIdx, ready: false, finished: false });
          this.broadcastState();
          break;
        }
        case "set-ready": {
          const p = this.state.players.find((x) => x.id === msg.from);
          if (p) p.ready = !!msg.ready;
          this.broadcastState();
          break;
        }
        case "finish":
          this.applyFinish(
            msg.from as string,
            msg as unknown as Parameters<MpClient["reportFinish"]>[0],
          );
          break;
        case "vote":
          this.applyVote(msg.from as string, msg.lessonId as string);
          break;
        case "set-quiz-style":
          this.state.quizStyle = (msg.quizStyle as string) ?? "default";
          this.broadcastState();
          break;
      }
    } else {
      // Joiner: only state messages from host matter.
      if (msg.type === "state") {
        this.state = msg.state as MpRoomState;
        this.emit({ type: "state", state: cloneState(this.state) });
      }
    }
  }

  private applyFinish(
    fromId: string,
    f: { timeMs: number; score: number; correctCount: number; totalQuestions: number },
  ) {
    if (!this.state) return;
    const p = this.state.players.find((x) => x.id === fromId);
    if (!p) return;
    p.finished = true;
    p.finishTimeMs = f.timeMs;
    p.finishScore = f.score;
    p.finishCorrect = f.correctCount;
    p.finishTotal = f.totalQuestions;
    // Once all real players finish (bots count as finished from the start
    // when added by host), move to results. Add a 12-second hard timeout too.
    const remaining = this.state.players.filter((q) => !q.finished && !q.isBot).length;
    if (remaining === 0 && this.state.phase === "racing") {
      this.state.phase = "results";
      this.beginVote();
    }
    this.broadcastState();
  }

  // ─── voting (host-authoritative) ────────────────────────────────────────

  private beginVote() {
    if (!this.state) return;
    this.state.voteOptions = this.pickVoteOptions();
    this.state.votes = {};
    this.state.voteDeadline = Date.now() + VOTE_MS;
    if (this.voteTimer != null) clearTimeout(this.voteTimer);
    this.voteTimer = window.setTimeout(() => this.resolveVote(), VOTE_MS);
  }

  private pickVoteOptions(): string[] {
    if (!this.state) return [];
    const cur = levelIndexOf(this.state.lessonId);
    const packMatch = /^pack:(.+?)::L\d+$/.exec(this.state.lessonId);
    const packId = packMatch ? packMatch[1] : null;
    const pool: number[] = [];
    for (let i = 1; i <= LEVEL_COUNT; i++) if (i !== cur) pool.push(i);
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j]!, pool[i]!];
    }
    const picks = pool.slice(0, 3).sort((a, b) => a - b);
    return picks.map((lvl) =>
      packId ? `pack:${packId}::L${lvl}` : this.state!.lessonId.replace(/L\d+$/i, `L${lvl}`),
    );
  }

  private applyVote(fromId: string, lessonId: string) {
    if (!this.isHost || !this.state) return;
    if (this.state.phase !== "results") return;
    if (!this.state.voteOptions?.includes(lessonId)) return;
    this.state.votes = this.state.votes ?? {};
    this.state.votes[fromId] = lessonId;
    this.broadcastState();
    // Unanimous → skip timer.
    const votes = this.state.votes;
    const humans = this.state.players.filter((p) => !p.isBot);
    if (humans.length > 0 && humans.every((p) => votes[p.id] != null)) {
      if (new Set(humans.map((p) => votes[p.id])).size === 1) this.resolveVote();
    }
  }

  private resolveVote() {
    if (this.voteTimer != null) { clearTimeout(this.voteTimer); this.voteTimer = null; }
    if (!this.state || this.state.phase !== "results") return;
    const options = this.state.voteOptions ?? [];
    if (options.length === 0) return;
    const votes = this.state.votes ?? {};
    let winner = options[0]!;
    let best = -1;
    for (const opt of options) {
      const c = Object.values(votes).filter((v) => v === opt).length;
      if (c > best) { best = c; winner = opt; }
    }
    if (best <= 0) winner = options[Math.floor(Math.random() * options.length)]!;
    this.state.lessonId = winner;
    this.clearVote();
    for (const p of this.state.players) {
      p.ready = true; p.finished = false;
      p.finishTimeMs = undefined; p.finishScore = undefined;
      p.finishCorrect = undefined; p.finishTotal = undefined;
    }
    this.state.phase = "countdown";
    this.state.countdownStartedAt = Date.now();
    this.broadcastState();
    setTimeout(() => {
      if (this.state?.phase === "countdown") {
        this.state.phase = "racing";
        this.broadcastState();
      }
    }, 3000);
  }

  private clearVote() {
    if (this.voteTimer != null) { clearTimeout(this.voteTimer); this.voteTimer = null; }
    if (!this.state) return;
    this.state.voteOptions = undefined;
    this.state.votes = undefined;
    this.state.voteDeadline = undefined;
  }
}

function levelIndexOf(lessonId: string): number | undefined {
  const m = /::L(\d+)$/.exec(lessonId) ?? /[-_]L(\d+)$/i.exec(lessonId);
  return m ? Number(m[1]) : undefined;
}

function cloneState(s: MpRoomState): MpRoomState {
  return {
    ...s,
    players: s.players.map((p) => ({ ...p })),
  };
}
